// Utility to update media detection for existing books
import firebaseService from '../firebase.service';
import { buildMediaIndex } from './fileUtils';

export const updateMediaDetectionForBooks = async (project, bookCodes, onProgress) => {
  console.log(`🔄 Updating media detection for ${bookCodes.length} books in ${project}`);
  
  const updatedBooks = [];
  let processedCount = 0;
  
  const timeoutPromise = new Promise((_, reject) => 
    setTimeout(() => reject(new Error('Media detection update timed out')), 300000)
  );
  
  const updatePromise = (async () => {
    // Build media index once (2 API calls) instead of per-book getDownloadURL
    const { lookup: mediaLookup } = await buildMediaIndex(project);

    const batchSize = 10;
    for (let i = 0; i < bookCodes.length; i += batchSize) {
      const batch = bookCodes.slice(i, i + batchSize);
      console.log(`Processing batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(bookCodes.length/batchSize)}`);
      
      const batchPromises = batch.map(async (bookCode) => {
        try {
          const mediaType = mediaLookup(bookCode);
          const hasCover = mediaType === 'image';
          const hasModel = mediaType === 'glb';
          
          const bookPath = `${project.toUpperCase()}/books/${bookCode}.json`;
          const bookUrl = await firebaseService.getFileUrl(bookPath);
          
          let bookData = {};
          
          if (bookUrl) {
            const response = await fetch(bookUrl, { cache: 'no-store' });
            bookData = await response.json();
            console.log(`📦 Found existing JSON for ${bookCode}`);
          } else {
            bookData = {
              code: bookCode,
              name_en: '',
              name_tc: '',
              name_sc: '',
              company: '',
              publisher_en: '',
              publisher_tc: '',
              publisher_sc: '',
              author_en: '',
              author_tc: '',
              author_sc: '',
              redirect_link: ''
            };
            console.log(`📝 Creating new JSON for ${bookCode}`);
          }
          
          const updatedBook = {
            ...bookData,
            mediaType,
            hasCover,
            hasModel,
            coverUrl: hasCover ? `https://firebasestorage.googleapis.com/v0/b/bookfair-infobox-qr.firebasestorage.app/o/${project.toUpperCase()}%2Fcovers%2F${bookCode}.webp?alt=media` : null,
            modelUrl: hasModel ? `https://firebasestorage.googleapis.com/v0/b/bookfair-infobox-qr.firebasestorage.app/o/${project.toUpperCase()}%2Fmodels%2F${bookCode}.glb?alt=media` : null,
            lastUpdated: new Date().toISOString()
          };
          
          const jsonContent = JSON.stringify(updatedBook, null, 2);
          const jsonBlob = new Blob([jsonContent], { type: 'application/json' });
          const jsonFile = new File([jsonBlob], `${bookCode}.json`, { type: 'application/json' });
          
          await firebaseService.uploadFile(jsonFile, bookPath);
          
          const result = {
            code: bookCode,
            mediaType,
            hasCover,
            hasModel
          };
          
          console.log(`✅ Updated ${bookCode}: ${mediaType}`);
          return result;
          
        } catch (error) {
          console.error(`❌ Error updating ${bookCode}:`, error);
          return null;
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      
      batchResults.forEach(result => {
        if (result) {
          updatedBooks.push(result);
        }
      });
      
      processedCount += batch.length;
      console.log(`Progress: ${processedCount}/${bookCodes.length}`);
      if (onProgress) {
        onProgress(processedCount, bookCodes.length);
      }
    }
    
    await updateMetadataWithMediaTypes(project, updatedBooks);
    
    console.log(`✅ Updated media detection for ${updatedBooks.length} books`);
    return updatedBooks;
  })();
  
  return await Promise.race([updatePromise, timeoutPromise]);
};

const updateMetadataWithMediaTypes = async (project, updatedBooks) => {
  try {
    const metadataPath = `${project.toUpperCase()}/books/metadata.json`;
    const metadataUrl = await firebaseService.getFileUrl(metadataPath);
    
    if (metadataUrl) {
      const response = await fetch(metadataUrl, { cache: 'no-store' });
      const metadata = await response.json();
      
      updatedBooks.forEach(updatedBook => {
        const bookInMetadata = metadata.books.find(book => book.code === updatedBook.code);
        if (bookInMetadata) {
          bookInMetadata.mediaType = updatedBook.mediaType;
          bookInMetadata.hasCover = updatedBook.hasCover;
          bookInMetadata.hasModel = updatedBook.hasModel;
        }
      });
      
      const jsonContent = JSON.stringify(metadata, null, 2);
      const jsonBlob = new Blob([jsonContent], { type: 'application/json' });
      const jsonFile = new File([jsonBlob], 'metadata.json', { type: 'application/json' });
      
      await firebaseService.uploadFile(jsonFile, metadataPath);
      console.log(`✅ Updated metadata.json with new media types`);
    }
  } catch (error) {
    console.error('❌ Error updating metadata:', error);
  }
};
